import React, { useState, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";

// SVG 및 아이콘 컴포넌트 import
import ReportSVG from "../components/ReportSVG";
import MapSVG from "../components/MapSVG";
import EquipmentSVG from "../components/EquipmentSVG";
import ReportIcon from "../components/ReportIcon";
import MapIcon from "../components/MapIcon";
import EquipmentIcon from "../components/EquipmentIcon";

const features = [
  { icon: ReportSVG, en: "Data Visualization", ko: "데이터 시각화", descEn: "Illustrating complex data with intuitive charts and graphs.", descKo: "직관적인 차트와 그래프로 복잡한 데이터를 표현합니다.", gradientId: "report-svg-gradient", gradientColors: ["#38bdf8", "#34d399"], bgColors: ["#eff6ff", "#cffafe"] },
  { icon: MapSVG, en: "Geographic Analysis", ko: "지리 정보 분석", descEn: "Pinpoint locations and analyze spatial data dynamically.", descKo: "위치를 특정하고 공간 데이터를 동적으로 분석합니다.", gradientId: "map-svg-gradient", gradientColors: ["#4ade80", "#10b981"], bgColors: ["#f0fdf4", "#dcfce7"] },
  { icon: EquipmentSVG, en: "Mechanical Systems", ko: "기계 시스템 관리", descEn: "Oversee interconnected systems with precision and clarity.", descKo: "상호 연결된 시스템을 정밀하고 명확하게 감독합니다.", gradientId: "equipment-svg-gradient", gradientColors: ["#fbbf24", "#f97316"], bgColors: ["#fff7ed", "#fef3c7"] },
  { icon: ReportIcon, en: "Insightful Reports", ko: "통찰력 있는 보고서", descEn: "Transform raw data into clear, actionable insights.", descKo: "정제되지 않은 데이터를 명확하고 실행 가능한 통찰력으로 변환하세요.", gradientId: "report-icon-gradient", gradientColors: ["#60a5fa", "#34d399"], bgColors: ["#e0f2fe", "#d1fae5"] },
  { icon: MapIcon, en: "Global Connectivity", ko: "글로벌 연결성", descEn: "Visualize your entire operation on a single, interactive globe.", descKo: "하나의 인터랙티브 글로브 위에서 모든 작업을 시각화하세요.", gradientId: "map-icon-gradient", gradientColors: ["#4ade80", "#2dd4bf"], bgColors: ["#dcfce7", "#ccfbf1"] },
  { icon: EquipmentIcon, en: "Systematic Rentals", ko: "체계적인 장비 대여", descEn: "Manage orbital assets with unparalleled efficiency.", descKo: "궤도형 자산을 비교할 수 없는 효율성으로 관리하세요.", gradientId: "equipment-icon-gradient", gradientColors: ["#facc15", "#fb923c"], bgColors: ["#fefce8", "#ffedd5"] }
];

const cardVariants = {
  enter: { y: 50, opacity: 0, scale: 0.95 },
  center: { y: 0, opacity: 1, scale: 1, transition: { duration: 0.5, ease: [0.2, 0.8, 0.2, 1] } },
  exit: { y: -50, opacity: 0, scale: 0.95, transition: { duration: 0.3, ease: [0.8, 0.2, 0.8, 0.2] } }
};

const textContainerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1, delayChildren: 0.2 } },
};

const textLineVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: { y: 0, opacity: 1, transition: { duration: 0.5, ease: "easeOut" } },
};

export default function Home() {
  const [page, setPage] = useState(0);

  const goToPage = useCallback((newPage) => {
    setPage((newPage + features.length) % features.length);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setPage(prev => (prev + 1) % features.length);
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  const activeFeature = features[page];
  const [startBg, endBg] = activeFeature.bgColors;
  const [activeColor] = activeFeature.gradientColors;

  return (
    <>
      <motion.main 
        className="relative min-h-screen w-full flex items-center justify-center overflow-hidden"
        animate={{ background: `linear-gradient(135deg, ${startBg}, ${endBg})` }}
        transition={{ duration: 1.2, ease: "easeInOut" }}
      >
        {/* 1. 화면 양쪽 끝에 배치된 새로운 '핸들' 버튼 */}
        <motion.button
            onClick={() => goToPage(page - 1)}
            className="absolute z-30 w-16 h-40 flex items-center justify-center pl-4 bg-transparent hover:bg-white/20 transition-colors duration-300 rounded-r-2xl"
            style={{ top: "50%", left: "calc(50% - 350px)", transform: "translateY(-50%)" }}
            whileHover={{ scale: 1.05, x: 5 }}
            whileTap={{ scale: 0.95 }}
            aria-label="Previous Page"
            >
            <ChevronLeft size={48} className="text-slate-700 opacity-70" />
            </motion.button>

            <motion.button
            onClick={() => goToPage(page + 1)}
            className="absolute z-30 w-16 h-40 flex items-center justify-center pr-4 bg-transparent hover:bg-white/20 transition-colors duration-300 rounded-l-2xl"
            style={{ top: "50%", right: "calc(50% - 350px)", transform: "translateY(-50%)" }}
            whileHover={{ scale: 1.05, x: -5 }}
            whileTap={{ scale: 0.95 }}
            aria-label="Next Page"
            >
            <ChevronRight size={48} className="text-slate-700 opacity-70" />
            </motion.button>
        
        {/* 중앙 콘텐츠 영역 */}
        <div className="w-full max-w-6xl flex flex-col items-center px-24">
          <AnimatePresence mode="wait">
            <motion.div
              key={page}
              variants={cardVariants}
              initial="enter"
              animate="center"
              exit="exit"
              className="grid grid-cols-1 md:grid-cols-9 bg-white/70 backdrop-blur-2xl rounded-3xl shadow-2xl overflow-hidden border border-white/50 w-full"
            >
              <motion.div
                className="col-span-1 md:col-span-5 bg-white p-8 md:p-12 flex flex-col justify-center text-center md:text-left order-2 md:order-1"
                variants={textContainerVariants}
                initial="hidden"
                animate="visible"
              >
                <motion.h1 variants={textLineVariants} className="text-5xl md:text-6xl font-extrabold text-slate-900 tracking-tight">{activeFeature.en}</motion.h1>
                <motion.h2 variants={textLineVariants} className="text-3xl md:text-4xl font-semibold text-slate-700 mt-3">{activeFeature.ko}</motion.h2>
                <motion.div variants={textLineVariants} className="mt-8 text-lg md:text-xl text-slate-600 leading-relaxed">
                  <p>{activeFeature.descEn}</p>
                  <p className="mt-2">{activeFeature.descKo}</p>
                </motion.div>
              </motion.div>
              <div className="col-span-1 md:col-span-4 min-h-[300px] md:min-h-0 flex items-center justify-center p-8 order-1 md:order-2">
                <div className="w-64 h-64">
                  <activeFeature.icon gradientId={activeFeature.gradientId} />
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* 하단 페이지 인디케이터 */}
        <footer className="absolute bottom-8 left-1/2 -translate-x-1/2 z-30">
          <div className="flex items-center space-x-2 bg-black/10 backdrop-blur-xl p-2 px-4 rounded-full shadow-lg border border-white/10">
            {features.map((_, index) => (
              <button key={index} onClick={() => goToPage(index)} className="p-1 rounded-full focus:outline-none focus:ring-2 focus:ring-white/50" aria-label={`Go to page ${index + 1}`}>
                <motion.div
                  className="h-2 rounded-full"
                  animate={{
                    width: page === index ? '24px' : '8px',
                    backgroundColor: page === index ? activeColor : 'rgba(255, 255, 255, 0.5)',
                  }}
                  transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                />
              </button>
            ))}
          </div>
        </footer>
      </motion.main>

      <svg width="0" height="0" style={{ position: 'absolute' }}>
        <defs>
          {features.map(f => (
            <linearGradient key={f.gradientId} id={f.gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor={f.gradientColors[0]} />
              <stop offset="100%" stopColor={f.gradientColors[1]} />
            </linearGradient>
          ))}
        </defs>
      </svg>
    </>
  );
}